import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import {
  buildPredictSnippet,
  copySnippet,
  downloadSnippet,
  snippetLangOptions,
  type SnippetLang,
} from "../lib/modelPredictSnippets";

type ModelSchema = {
  status?: string;
  session_id?: string;
  target_feature?: string;
  predictor_features?: string[];
  production_ready?: boolean;
  predict_url?: string;
  human_summary?: { headline?: string; body?: string };
  body_example?: Record<string, number>;
};

type PredictResult = {
  status?: string;
  prediction?: number;
  target_feature?: string;
  confidence?: number;
  interpretation?: string;
  error?: string;
};

function userFacingError(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

function resolveApiBase(): string {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

/**
 * Customer landing page for purchased / inquired model access.
 * URL: /models/predict?session=<train_session_id>&package=...&order=...
 * Loads schema for the right model, posts feature values, shows response
 * (same behavior as step 3 “Try a prediction”).
 */
export function CustomerModelPredictPage() {
  const [searchParams] = useSearchParams();
  const sessionId = (searchParams.get("session") || searchParams.get("session_id") || "").trim();
  const packageId = (searchParams.get("package") || "").trim();
  const orderId = (searchParams.get("order") || "").trim();

  const [schema, setSchema] = useState<ModelSchema | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [predictError, setPredictError] = useState<string | null>(null);
  const [prediction, setPrediction] = useState<PredictResult | null>(null);
  const [snippetLang, setSnippetLang] = useState<SnippetLang>("python");
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  const features = schema?.predictor_features || [];
  const predictPath =
    schema?.predict_url ||
    (sessionId ? `/api/train/models/${encodeURIComponent(sessionId)}/predict` : "");

  useEffect(() => {
    if (!sessionId) {
      setLoadError("Missing session in the URL. Use ?session=<model_session_id>.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    apiFetch(`/api/train/models/${encodeURIComponent(sessionId)}`, {
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as ModelSchema & { error?: string };
        if (!res.ok) {
          throw new Error(data.error || "Model not found. Train again or check the session id.");
        }
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        setSchema(data);
        const next: Record<string, string> = {};
        for (const f of data.predictor_features || []) {
          const ex = data.body_example?.[f];
          next[f] = ex != null ? String(ex === 0 ? 1 : ex) : "1";
        }
        setInputs(next);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(userFacingError(err, "Could not load this model."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const bodyExample = useMemo(() => {
    const body: Record<string, number> = {};
    for (const f of features) {
      const raw = (inputs[f] ?? schema?.body_example?.[f] ?? "1").toString().trim();
      const n = Number(raw);
      body[f] = Number.isFinite(n) ? n : 1;
    }
    if (!Object.keys(body).length && schema?.body_example) {
      return schema.body_example;
    }
    return Object.keys(body).length ? body : { feature: 1.0 };
  }, [features, inputs, schema?.body_example]);

  const apiExample = useMemo(() => {
    if (!predictPath) return "";
    return `POST ${predictPath}\nContent-Type: application/json\n\n${JSON.stringify(bodyExample, null, 2)}`;
  }, [predictPath, bodyExample]);

  const snippetMeta = useMemo(
    () => snippetLangOptions().find((l) => l.id === snippetLang) || snippetLangOptions()[0],
    [snippetLang],
  );

  const snippetCode = useMemo(
    () =>
      buildPredictSnippet(snippetLang, {
        apiBase: resolveApiBase(),
        sessionId: sessionId || "YOUR_SESSION_ID",
        bodyExample,
        packageId,
      }),
    [snippetLang, sessionId, bodyExample, packageId],
  );

  async function onPredict(e: FormEvent) {
    e.preventDefault();
    if (!predictPath) return;
    setBusy(true);
    setPredictError(null);
    setPrediction(null);
    try {
      const body: Record<string, number> = {};
      for (const f of features) {
        const raw = (inputs[f] ?? "").trim();
        if (!raw) throw new Error(`Please enter a value for ${f}.`);
        const n = Number(raw);
        if (!Number.isFinite(n)) throw new Error(`${f} must be a number.`);
        body[f] = n;
      }
      const res = await apiFetch(predictPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });
      const data = (await res.json().catch(() => ({}))) as PredictResult;
      if (!res.ok || data.status === "error") {
        throw new Error(data.error || data.interpretation || "Prediction failed.");
      }
      setPrediction(data);
    } catch (err) {
      setPredictError(userFacingError(err, "Prediction failed."));
    } finally {
      setBusy(false);
    }
  }

  async function onCopySnippet() {
    const ok = await copySnippet(snippetCode);
    setCopyStatus(ok ? "Copied to clipboard." : "Could not copy — select the script manually.");
    window.setTimeout(() => setCopyStatus(null), 2500);
  }

  function onDownloadSnippet() {
    downloadSnippet(snippetMeta.filename, snippetCode);
    setCopyStatus(`Downloaded ${snippetMeta.filename}`);
    window.setTimeout(() => setCopyStatus(null), 2500);
  }

  return (
    <article className="page page--wide customer-model-predict">
      <p className="eyebrow">Model Access</p>
      <h1 className="deposit-heading">Try your AI model</h1>
      <p className="action-form-lead">
        Post feature values to your trained model and get a response — same as step 3 on the home
        train form.
      </p>

      {(packageId || orderId || sessionId) && (
        <p className="customer-model-predict-meta muted">
          {packageId ? (
            <>
              Package: <strong>{packageId}</strong>
              {" · "}
            </>
          ) : null}
          {sessionId ? (
            <>
              Session: <code>{sessionId}</code>
            </>
          ) : null}
          {orderId ? (
            <>
              {" · "}Order: <code>{orderId.slice(0, 8)}…</code>
            </>
          ) : null}
        </p>
      )}

      {loading && <p className="muted page-loading">Loading model…</p>}

      {loadError && !loading && (
        <p className="model-run-error" role="alert">
          {loadError}
        </p>
      )}

      {!loading && !loadError && schema && (
        <section className="action-form-card customer-model-predict-card">
          {schema.human_summary?.headline && (
            <p className="train-ready-line">
              <strong>{schema.human_summary.headline}</strong>
            </p>
          )}
          {schema.human_summary?.body && (
            <p className="buy-server-product-note">{schema.human_summary.body}</p>
          )}
          {!schema.production_ready && (
            <p className="model-run-error" role="status">
              This model is not marked production-ready; the API may reject predictions.
            </p>
          )}

          <form className="deposit-form buy-server-form train-predict-form" onSubmit={onPredict}>
            <div className="train-predict-row">
              {features.map((f) => (
                <div className="train-predict-field" key={f}>
                  <label htmlFor={`cmp-${f}`}>{f}</label>
                  <input
                    id={`cmp-${f}`}
                    type="text"
                    inputMode="decimal"
                    className="train-predict-input"
                    value={inputs[f] ?? ""}
                    onChange={(e) => setInputs((prev) => ({ ...prev, [f]: e.target.value }))}
                    required
                  />
                </div>
              ))}
              <button type="submit" className="btn btn-primary train-predict-submit" disabled={busy}>
                {busy ? "Predicting…" : "Get model response"}
              </button>
            </div>
          </form>

          {predictError && (
            <p className="model-run-error" role="alert">
              {predictError}
            </p>
          )}

          {prediction && (
            <div className="deposit-success train-predict-result">
              <div className="train-predict-result-line">
                <h3 className="train-predict-result-title">Model response</h3>
                <p className="train-predict-result-text">
                  {prediction.interpretation || (
                    <>
                      Predicted <strong>{prediction.target_feature}</strong>:{" "}
                      {prediction.prediction != null ? prediction.prediction.toFixed(2) : "—"}
                    </>
                  )}
                </p>
              </div>
            </div>
          )}

          <details className="customer-model-predict-api">
            <summary>API endpoint (query / path uses this session)</summary>
            <pre className="customer-model-predict-api-pre">{apiExample}</pre>
            <p className="muted">
              Schema: <code>/api/train/models/{sessionId}</code>
              <br />
              Predict: <code>POST /api/train/models/{sessionId}/predict</code>
            </p>
          </details>
        </section>
      )}

      {sessionId ? (
        <section
          className="action-form-card customer-model-predict-scripts"
          aria-label="Website integration scripts"
        >
          <h2 className="train-subhead">Add to your website</h2>
          <p className="buy-server-product-note">
            Select a language, copy or download the script, and put it on your site. Replace{" "}
            <code>SESSION_ID</code> / <code>API_BASE</code> if needed. Expected result includes{" "}
            <code>prediction</code>, <code>target_feature</code>, and <code>interpretation</code>.
          </p>

          <div className="customer-snippet-lang-row" role="tablist" aria-label="Script language">
            {snippetLangOptions().map((lang) => (
              <button
                key={lang.id}
                type="button"
                role="tab"
                aria-selected={snippetLang === lang.id}
                className={`btn customer-snippet-lang${
                  snippetLang === lang.id ? " customer-snippet-lang--active" : ""
                }`}
                onClick={() => setSnippetLang(lang.id)}
              >
                {lang.label}
              </button>
            ))}
          </div>

          <div className="customer-snippet-actions">
            <button type="button" className="btn btn-primary" onClick={() => void onCopySnippet()}>
              Copy script
            </button>
            <button type="button" className="btn" onClick={onDownloadSnippet}>
              Download {snippetMeta.filename}
            </button>
            {copyStatus ? (
              <span className="customer-snippet-status" role="status">
                {copyStatus}
              </span>
            ) : null}
          </div>

          <pre className="customer-model-predict-api-pre customer-snippet-pre">{snippetCode}</pre>
        </section>
      ) : null}

      <p className="customer-model-predict-back">
        <Link to="/" className="card-link">
          ← Back home
        </Link>
      </p>
    </article>
  );
}
